import { Gym, GymKey, GymKeyHolder } from '@hefe/models';
import {
  createAsyncThunk,
  createSelector,
  createSlice,
  PayloadAction,
} from '@reduxjs/toolkit';
import API from '../../../api/api';
import {
  GymCreateUpdateRequest,
  GymKeyCreateUpdateRequest,
  GymKeyHolderCreateUpdateRequest,
} from '../../../models/requests/base/gym';
import { isCacheValid } from '../../../utils/cache/caching';

const FEATURE_KEY = 'gyms';

/************************************************************************************
 *
 *                                  ACTIONS
 *
 ************************************************************************************
 */
export const fetchGyms = createAsyncThunk(
  'gyms/getAll',
  async (data: { cacheDuration: number }, thunkAPI) => {
    if (!isCacheValid('gyms/getAll', data, thunkAPI)) {
      const response = await API.gyms().getAll();
      return response?.data;
    }
  }
);

export const createGym = createAsyncThunk(
  'gyms/create',
  async (data: GymCreateUpdateRequest, thunkAPI) => {
    const response = await API.gyms().create(data);
    return response?.data;
  }
);

export const updateGym = createAsyncThunk(
  'gyms/update',
  async (data: GymCreateUpdateRequest, thunkAPI) => {
    const response = await API.gyms().update(data);
    return response?.data;
  }
);

export const deleteGym = createAsyncThunk<any, Gym>(
  'gyms/delete',
  async (gym: Gym, thunkAPI) => {
    const response = await API.gyms().delete(gym);
    if (response) {
      return thunkAPI.fulfillWithValue(gym);
    } else {
      return thunkAPI.rejectWithValue(null);
    }
  }
);

/*************************************************************************************
 *                                 GYM KEYS ACTIONS
 *************************************************************************************/
export const fetchKeys = createAsyncThunk(
  'gyms/keys/getAll',
  async (thunkAPI) => {
    const response = await API.keys().getAll();
    return response?.data;
  }
);

export const createKey = createAsyncThunk(
  'gyms/keys/create',
  async (data: GymKeyCreateUpdateRequest, thunkAPI) => {
    const response = await API.keys().create(data);
    return response?.data;
  }
);

export const updateKey = createAsyncThunk(
  'gyms/keys/update',
  async (data: GymKeyCreateUpdateRequest, thunkAPI) => {
    const response = await API.keys().update(data);
    return response?.data;
  }
);

export const deleteKey = createAsyncThunk<any, GymKey>(
  'gyms/keys/delete',
  async (key: GymKey, thunkAPI) => {
    const response = await API.keys().delete(key);
    if (response) {
      return thunkAPI.fulfillWithValue(key);
    } else {
      return thunkAPI.rejectWithValue(null);
    }
  }
);

/*************************************************************************************
 *                                 GYM KEY HOLDER ACTIONS
 *************************************************************************************/
export const createKeyHolder = createAsyncThunk(
  'gyms/keys/holders/create',
  async (data: GymKeyHolderCreateUpdateRequest, thunkAPI) => {
    const response = await API.keys().holders(data.key_id).create(data);
    return {
      key_id: data.key_id,
      data: response?.data,
    };
  }
);

export const updateKeyHolder = createAsyncThunk(
  'gyms/keys/holders/update',
  async (data: GymKeyHolderCreateUpdateRequest, thunkAPI) => {
    const response = await API.keys().holders(data.key_id).update(data);
    return response?.data;
  }
);

export const deleteKeyHolder = createAsyncThunk<any, GymKeyHolder>(
  'gyms/keys/holders/delete',
  async (holder: GymKeyHolder, thunkAPI) => {
    const response = await API.keys().holders(holder.key_id).delete(holder);
    if (response) {
      return thunkAPI.fulfillWithValue(holder);
    } else {
      return thunkAPI.rejectWithValue(null);
    }
  }
);

/************************************************************************************
 *
 *                                      SLICE
 *
 ************************************************************************************
 */

export interface GymsState {
  gyms: Gym[];
  keys: GymKey[];
}

const initialState: GymsState = {
  gyms: [],
  keys: [],
};

export const gymsSlice = createSlice({
  name: FEATURE_KEY,
  initialState: initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder.addCase(
      fetchGyms.fulfilled,
      (state: GymsState, action: PayloadAction<Gym[]>) => {
        if (action.payload) {
          state.gyms = action.payload;
          state.gyms.sort((a: Gym, b: Gym) => a.name.localeCompare(b.name));
        }
      }
    );
    builder.addCase(
      createGym.fulfilled,
      (state: GymsState, action: PayloadAction<Gym>) => {
        state.gyms.push(action.payload);
        state.gyms.sort((a: Gym, b: Gym) => a.name.localeCompare(b.name));
      }
    );
    builder.addCase(
      updateGym.fulfilled,
      (state: GymsState, action: PayloadAction<Gym>) => {
        state.gyms = state.gyms.map((gym) =>
          gym.id === action.payload.id ? action.payload : gym
        );
      }
    );
    builder.addCase(
      deleteGym.fulfilled,
      (state: GymsState, action: PayloadAction<Gym>) => {
        state.gyms = state.gyms.filter((gym) => gym.id !== action.payload.id);
      }
    );
    /*************************************************************************************
     *                                 GYM KEYS REDUCERS
     *************************************************************************************/
    builder.addCase(
      fetchKeys.fulfilled,
      (state: GymsState, action: PayloadAction<GymKey[]>) => {
        state.keys = action.payload;
      }
    );
    builder.addCase(
      createKey.fulfilled,
      (state: GymsState, action: PayloadAction<GymKey>) => {
        state.keys.push(action.payload);
      }
    );
    builder.addCase(
      updateKey.fulfilled,
      (state: GymsState, action: PayloadAction<GymKey>) => {
        state.keys = state.keys.map((key) =>
          key.id === action.payload.id ? action.payload : key
        );
      }
    );
    builder.addCase(
      deleteKey.fulfilled,
      (state: GymsState, action: PayloadAction<GymKey>) => {
        state.keys = state.keys.filter((key) => key.id !== action.payload.id);
      }
    );
    /*************************************************************************************
     *                                 GYM KEY HOLDERS REDUCERS
     *************************************************************************************/
    builder.addCase(
      createKeyHolder.fulfilled,
      (
        state: GymsState,
        action: PayloadAction<{ key_id: number; data: GymKeyHolder[] }>
      ) => {
        const keyIndex = state.keys.findIndex(
          (key) => key.id === action.payload.key_id
        );
        if (keyIndex > -1) {
          state.keys[keyIndex].holders = action.payload.data;
        }
      }
    );
    builder.addCase(
      updateKeyHolder.fulfilled,
      (state: GymsState, action: PayloadAction<GymKeyHolder>) => {
        const keyIndex = state.keys.findIndex(
          (key) => key.id === action.payload.key_id
        );

        if (keyIndex > -1) {
          state.keys[keyIndex].holders = state.keys[keyIndex].holders?.map(
            (holder) =>
              holder.id === action.payload.id ? action.payload : holder
          );
        }
      }
    );
    builder.addCase(
      deleteKeyHolder.fulfilled,
      (state: GymsState, action: PayloadAction<GymKeyHolder>) => {
        const keyIndex = state.keys.findIndex(
          (key) => key.id === action.payload.key_id
        );
        if (keyIndex > -1) {
          state.keys[keyIndex].holders = state.keys[keyIndex].holders?.filter(
            (holder) => holder.id !== action.payload.id
          );
        }
      }
    );
  },
});

/*
 * Export reducer for store configuration.
 */
export const gymsReducer = gymsSlice.reducer;
export const gymsActions = gymsSlice.actions;

const state = (rootState: any): GymsState => rootState[FEATURE_KEY];

export const gymsSelector = createSelector(state, (state) => {
  return state.gyms;
});

export const keysSelector = createSelector(state, (state) => {
  return state.keys;
});

export const gymsSelectSelector = createSelector(state, (state) => {
  return state.gyms?.map((gym) => {
    return {
      value: gym.id,
      label: gym.name,
    };
  });
});
